import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { Archive, Building2, Plus, Edit as EditIcon, Delete as DeleteIcon } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export default function AssetSuppliers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState({
    name: '',
    vat_number: '',
    tax_code: '',
    address: '',
    zip_code: '',
    city: '',
    province: '',
    country: '',
    email: '',
    phone: '',
    note: ''
  });

  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/asset-suppliers');
      setRows(res.data || []);
    } catch (error) {
      console.error('Error loading asset suppliers', error);
      toast.error('Errore nel caricamento dei fornitori asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredSortedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let data = rows;
    if (term) {
      data = data.filter((row) => {
        const name = row.name || '';
        const vat = row.vat_number || '';
        const tax = row.tax_code || '';
        const city = row.city || '';
        const note = row.note || '';
        return (
          name.toLowerCase().includes(term) ||
          vat.toLowerCase().includes(term) ||
          tax.toLowerCase().includes(term) ||
          city.toLowerCase().includes(term) ||
          note.toLowerCase().includes(term)
        );
      });
    }
    const sorted = [...data].sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, search, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSortedRows.slice(start, start + rowsPerPage);
  }, [filteredSortedRows, page, rowsPerPage]);

  const openNewDialog = () => {
    setEditingRow(null);
    setForm({
      name: '',
      vat_number: '',
      tax_code: '',
      address: '',
      zip_code: '',
      city: '',
      province: '',
      country: '',
      email: '',
      phone: '',
      note: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditingRow(row);
    setForm({
      name: row.name || '',
      vat_number: row.vat_number || '',
      tax_code: row.tax_code || '',
      address: row.address || '',
      zip_code: row.zip_code || '',
      city: row.city || '',
      province: row.province || '',
      country: row.country || '',
      email: row.email || '',
      phone: row.phone || '',
      note: row.note || ''
    });
    setDialogOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Il nome del fornitore è obbligatorio');
      return;
    }
    try {
      const payload = {
        name: form.name,
        vat_number: form.vat_number || null,
        tax_code: form.tax_code || null,
        address: form.address || null,
        zip_code: form.zip_code || null,
        city: form.city || null,
        province: form.province || null,
        country: form.country || null,
        email: form.email || null,
        phone: form.phone || null,
        note: form.note || null
      };
      if (editingRow) {
        await api.put(`/asset-suppliers/${editingRow.id}`, payload);
        toast.success('Fornitore aggiornato');
      } else {
        await api.post('/asset-suppliers', payload);
        toast.success('Fornitore creato');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving asset supplier', error);
      toast.error('Errore nel salvataggio del fornitore asset');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/asset-suppliers/${deleteTarget.id}`);
      toast.success('Fornitore eliminato');
      closeDeleteDialog();
      loadData();
    } catch (error) {
      console.error('Error deleting asset supplier', error);
      const msg =
        error?.response?.data?.message ||
        'Errore nell\'eliminazione del fornitore asset';
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Building2 size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Fornitori asset
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestisci l&apos;anagrafica dei fornitori utilizzati per gli asset e le manutenzioni
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" size="small" onClick={openNewDialog}>
          <Plus size={16} style={{ marginRight: 8 }} />
          Nuovo fornitore
        </Button>
      </Box>

      <StyledCard>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <TextField
            size="small"
            placeholder="Cerca per nome o note..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'name' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Ragione Sociale
                  </TableSortLabel>
                </TableCell>
                <TableCell>Partita IVA</TableCell>
                <TableCell>Codice fiscale</TableCell>
                <TableCell>Città</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefono</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredSortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun fornitore definito.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.vat_number || '-'}</TableCell>
                    <TableCell>{row.tax_code || '-'}</TableCell>
                    <TableCell>{row.city || '-'}</TableCell>
                    <TableCell>{row.email || '-'}</TableCell>
                    <TableCell>{row.phone || '-'}</TableCell>
                    <TableCell>{row.note || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(row)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(row)}
                      >
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredSortedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingRow ? 'Modifica fornitore' : 'Nuovo fornitore'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ragione Sociale"
              size="small"
              value={form.name}
              onChange={handleFormChange('name')}
              fullWidth
              required
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Partita IVA"
                size="small"
                value={form.vat_number}
                onChange={handleFormChange('vat_number')}
                fullWidth
              />
              <TextField
                label="Codice fiscale"
                size="small"
                value={form.tax_code}
                onChange={handleFormChange('tax_code')}
                fullWidth
              />
            </Box>
            <TextField
              label="Indirizzo"
              size="small"
              value={form.address}
              onChange={handleFormChange('address')}
              fullWidth
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: 2 }}>
              <TextField
                label="CAP"
                size="small"
                value={form.zip_code}
                onChange={handleFormChange('zip_code')}
                fullWidth
              />
              <TextField
                label="Città"
                size="small"
                value={form.city}
                onChange={handleFormChange('city')}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Provincia"
                size="small"
                value={form.province}
                onChange={handleFormChange('province')}
                fullWidth
              />
              <TextField
                label="Nazione"
                size="small"
                value={form.country}
                onChange={handleFormChange('country')}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Email"
                size="small"
                value={form.email}
                onChange={handleFormChange('email')}
                fullWidth
              />
              <TextField
                label="Telefono"
                size="small"
                value={form.phone}
                onChange={handleFormChange('phone')}
                fullWidth
              />
            </Box>
            <TextField
              label="Note"
              size="small"
              value={form.note}
              onChange={handleFormChange('note')}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare il fornitore:
          </DialogContentText>
          <Box sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            Città: {deleteTarget?.city || '-'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
